/**
 * =============================================================================
 * Institution admin provisioning — shared form pieces (FE #27 / BE #61)
 * =============================================================================
 *
 * - <AdminProvisionFields>  e-mail / optional name / invite language
 * - <ProvisionConflictPanel> 409 from the BE: existing user -> offer to assign
 *                           (with a notification mail) or continue without
 * - <OutcomeBanner>        success / error banner (texts: utils/institutionAdmin)
 *
 * Used by the "Create institution" form (first admin, default on) and by the
 * "Add admin" action on existing institutions.
 */

import type { AdminProvisionState, OutcomeMessage } from '../utils/institutionAdmin'
import { type ProvisionConflict, roleLabel, ut } from '../utils/usersI18n'

interface AdminProvisionFieldsProps {
  value: AdminProvisionState
  onChange: (value: AdminProvisionState) => void
  /** Shown when the e-mail input is empty (e.g. institution contact e-mail). */
  emailPlaceholder?: string
  testIdPrefix: string
}

export const AdminProvisionFields: React.FC<AdminProvisionFieldsProps> = ({
  value,
  onChange,
  emailPlaceholder,
  testIdPrefix,
}) => (
  <div className="space-y-3">
    <label className="block text-sm text-gray-700">
      {ut('adminEmailLabel')}
      <input
        type="email"
        className="input mt-1"
        required
        data-testid={`${testIdPrefix}-admin-email`}
        value={value.email}
        placeholder={emailPlaceholder || 'admin@nemocnica.sk'}
        onChange={(e) => onChange({ ...value, email: e.target.value })}
      />
    </label>
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <label className="block text-sm text-gray-700">
        {ut('adminFirstName')}
        <input
          className="input mt-1"
          data-testid={`${testIdPrefix}-admin-first-name`}
          value={value.first_name}
          onChange={(e) => onChange({ ...value, first_name: e.target.value })}
        />
      </label>
      <label className="block text-sm text-gray-700">
        {ut('adminLastName')}
        <input
          className="input mt-1"
          data-testid={`${testIdPrefix}-admin-last-name`}
          value={value.last_name}
          onChange={(e) => onChange({ ...value, last_name: e.target.value })}
        />
      </label>
    </div>
    <p className="text-xs text-gray-500">{ut('adminNameHint')}</p>
    <label className="block text-sm text-gray-700">
      {ut('inviteLanguage')}
      <select
        className="input mt-1"
        data-testid={`${testIdPrefix}-admin-language`}
        value={value.language}
        onChange={(e) => onChange({ ...value, language: e.target.value as 'sk' | 'en' })}
      >
        <option value="sk">Slovenčina</option>
        <option value="en">English</option>
      </select>
    </label>
    <p className="text-xs text-gray-500">{ut('firstAdminHint')}</p>
  </div>
)

interface ProvisionConflictPanelProps {
  conflict: ProvisionConflict
  busy?: boolean
  onAssign: () => void
  /** Offered on institution create only (continue without an admin). */
  onSkip?: () => void
  onCancel: () => void
}

export const ProvisionConflictPanel: React.FC<ProvisionConflictPanelProps> = ({
  conflict,
  busy,
  onAssign,
  onSkip,
  onCancel,
}) => {
  const user = conflict.user
  const email = user?.email || ''
  if (conflict.code === 'user_not_assignable') {
    return (
      <div
        className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        role="alert"
        data-testid="provision-conflict"
      >
        {ut('userNotAssignable', { email, role: user?.role })}
      </div>
    )
  }
  return (
    <div
      className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900"
      role="alert"
      data-testid="provision-conflict"
    >
      <p className="font-semibold">{ut('userExistsTitle')}</p>
      <p>
        {ut('userExistsBody', {
          email,
          role: roleLabel(user?.role),
          institution: user?.institution_name || ut('userExistsNoInstitution'),
        })}
      </p>
      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={busy}>
          {ut('cancel')}
        </button>
        {onSkip && (
          <button
            type="button"
            className="btn-secondary"
            data-testid="provision-skip"
            onClick={onSkip}
            disabled={busy}
          >
            {ut('skipAdminAction')}
          </button>
        )}
        <button
          type="button"
          className="btn-primary"
          data-testid="provision-assign"
          onClick={onAssign}
          disabled={busy}
        >
          {busy ? ut('submitting') : ut('assignAction')}
        </button>
      </div>
    </div>
  )
}

export const OutcomeBanner: React.FC<{ outcome: OutcomeMessage | null; onClose?: () => void }> = ({
  outcome,
  onClose,
}) => {
  if (!outcome) return null
  const cls =
    outcome.severity === 'success'
      ? 'border-green-200 bg-green-50 text-green-700'
      : 'border-red-200 bg-red-50 text-red-700'
  return (
    <div
      className={`flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${cls}`}
      role={outcome.severity === 'error' ? 'alert' : 'status'}
      data-testid="outcome-banner"
    >
      <span>{outcome.text}</span>
      {onClose && (
        <button type="button" className="text-xs underline" onClick={onClose}>
          ×
        </button>
      )}
    </div>
  )
}
